import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
	InputLabel,
	FormControl,
	Button,
	Typography,
	Grid,
	Paper,
	Select,
	MenuItem,
	Icon,
	FormHelperText,
} from '@mui/material';
import { constants } from '../../constants/Constants';
import history from '../../router/history';
import { Theme } from '@mui/material/styles';
import TemplateDetails from './TemplateDetails';
import { getDeviceTypes } from '../../services/devices.service';
import { ACCESS_MODE, Advertisement } from '@horasys/bellplus/common/types';
import { Template } from '@horasys/bellplus/common/types';
import { DeviceType } from '@horasys/bellplus/common/types';
import theme from 'theme';
import { CssTextField } from 'views/Common/CssTextField';
import { CssSelect } from 'views/Common/CssSelect';

const PREFIX = 'AdvertisementDetails';

const classes = {
    formElement: `${PREFIX}-formElement`,
    uploadInput: `${PREFIX}-uploadInput`,
    uploadWrapper: `${PREFIX}-uploadWrapper`,
    formContainer: `${PREFIX}-formContainer`,
    saveGrid: `${PREFIX}-saveGrid`,
    templateDetails: `${PREFIX}-templateDetails`,
    form: `${PREFIX}-form`,
    errorMessage: `${PREFIX}-errorMessage`
};

const StyledGrid = styled(Grid)((
    {
        theme: Theme
    }
) => ({
    [`& .${classes.formElement}`]: {
        marginTop: theme.spacing(2),
        minWidth: '100%',
    },

    [`& .${classes.uploadInput}`]: {
        fontSize: '100px',
        position: 'absolute',
        left: 0,
        top: 0,
        opacity: 0,
    },

    [`& .${classes.uploadWrapper}`]: {
        position: 'relative',
        overflow: 'hidden',
        display: 'inline-block',
    },

    [`& .${classes.formContainer}`]: {
        padding: theme.spacing(2),
    },

    [`& .${classes.saveGrid}`]: {
        marginTop: theme.spacing(2),
    },

    [`& .${classes.templateDetails}`]: {
        marginTop: theme.spacing(2),
    },

    [`& .${classes.form}`]: {
        marginTop: theme.spacing(4),
    },

    [`& .${classes.errorMessage}`]: {
        color: 'red',
    }
}));

interface Props {
	viewMode: any;
	templatesList: any;
	createAdvertisement: any;
	handleInputDetails: any;
	errorObj: any;
	advertisement: Advertisement;
	isOpenViewModal: boolean;
	mode: ACCESS_MODE;
}
/**
 * Component for rendering Advertising Details - in Create Mode, Edit Mode, View Mode with submit button
 * @param props
 */
const AdvertisementDetails = (props: Props) => {
	const {
		advertisement,
		viewMode,
		templatesList,
		createAdvertisement,
		handleInputDetails,
		errorObj,
		mode,
	} = props;
	const cancelClient = () => {
		history.back();
	};
	const submitForm = (e: any) => {
		e.preventDefault();
		createAdvertisement();
	};


	const [deviceTypeList, setDeviceTypeList] = useState([]);

	// Get DeviceType List
	useEffect(() => {
		const getDeviceTypeList = async () => {
			const deviceListObjectList = await getDeviceTypes();

			setDeviceTypeList(deviceListObjectList);
		};

		getDeviceTypeList();
	}, []);

	const transformRatio = (input: any, ratio: any, defaultValue: any) => {
		const imgWidth = parseInt(input ? input.toString() : defaultValue) / ratio + 'px';
		return imgWidth;
	};

	return (
        <StyledGrid container style={{padding:'20px'}}>
			<Grid item xs={12}>
				<Paper elevation={3}>
					<Grid className={classes.formContainer}>
						<Typography
							variant="h3"
							component="h3"
							align="center"
              style={{fontSize:'1.6rem'}}
							className={classes.formElement}>
							Advertisement details
						</Typography>
						<form onSubmit={submitForm} className={classes.form} noValidate>
							<Grid container spacing={3}>
								<Grid item xs={8}>
									<FormControl
										variant="outlined"
										className={classes.formElement}
										disabled={false}>
										<CssTextField
											label="Advertisement Name"
											name="advertisementName"
											variant="outlined"
											fullWidth
											autoComplete="off"
											value={advertisement.name}
											onChange={(e) => {
												handleInputDetails(e);
											}}
											disabled={viewMode}
										/>
										<FormHelperText
											error={true}
											className={classes.errorMessage}>
											{errorObj.name}
										</FormHelperText>
									</FormControl>
									<FormControl
										variant="outlined"
										className={classes.formElement}
										disabled={viewMode}>
										<InputLabel htmlFor="deviceType">
											Device Type
										</InputLabel>
										<CssSelect
											name="deviceType"
											id="deviceType"
											label="Device Type"
											value={
												advertisement.deviceType.id
													? advertisement.deviceType.id
													: ''
											}
											onChange={handleInputDetails}
											disabled={viewMode || mode === 'EDIT'}>
											{deviceTypeList != null &&
												deviceTypeList.map(
													(deviceType: DeviceType) => (
														<MenuItem
															value={deviceType.id}
															key={deviceType.id}>
															{deviceType.deviceType}
														</MenuItem>
													),
												)}
										</CssSelect>
										<FormHelperText
											error={true}
											className={classes.errorMessage}>
											{errorObj.deviceType &&
												errorObj.deviceType.id}
										</FormHelperText>
									</FormControl>

									<FormControl
										variant="outlined"
										className={classes.formElement}>
										<InputLabel htmlFor="templateId">
											Select Template
										</InputLabel>
										<CssSelect
											name="templateId"
											id="templateId"
											label="Select Template"
											required
											value={
												advertisement.template.id
													? advertisement.template.id
													: ''
											}
											onChange={handleInputDetails}
											disabled={viewMode || mode === 'EDIT'}>
											{templatesList != null &&
												templatesList.map((data: Template) => (
													<MenuItem
														value={data.id}
														key={data.id}>
														{data.name}
													</MenuItem>
												))}
										</CssSelect>
										<FormHelperText
											error={true}
											className={classes.errorMessage}>
											{errorObj.template && errorObj.template.id}
										</FormHelperText>
									</FormControl>
								</Grid>
								<Grid item xs={4}>
									{advertisement.templateAssets.length > 0 ? (
										<>
											<img
												src={advertisement.template.templateUrl}
												width={transformRatio(
													advertisement.deviceType.width,
													4,
													300,
												)}
												alt="Template thumbnail view"
												height={transformRatio(
													advertisement.deviceType.height,
													4,
													300,
												)}
											/>{' '}
										</>
									) : (
										<> </>
									)}
								</Grid>
							</Grid>
							<div className={classes.templateDetails}>
								{advertisement.templateAssets.length > 0 ? (
									<>
										<TemplateDetails
											advertisement={advertisement}
											viewMode={viewMode}
											errorObj={errorObj}
											mode={mode}
										/>{' '}
									</>
								) : (
									<> </>
								)}
							</div>
							<Grid
								container
								item
								direction="row"
								justifyContent="space-between"
								className={classes.saveGrid}>
								<Grid item>
									<Button
										className={classes.formElement}
                    style={{ color:'rgba(0, 0, 0, 0.87)' }}
										onClick={cancelClient}>
										{constants.CANCEL}
									</Button>
								</Grid>
								<Grid item>
									<Button
										className={classes.formElement}
                    style={{ backgroundColor: '#e0e0e0' , color:'rgba(0, 0, 0, 0.87)' }}
										disabled={viewMode}
										variant="contained"
										type="submit"
										endIcon={<Icon>send</Icon>}>
										{constants.SAVE}
									</Button>
								</Grid>
							</Grid>
						</form>
					</Grid>
				</Paper>
			</Grid>
		</StyledGrid>
    );
};

export default AdvertisementDetails;
